import {
  Box,
  Heading,
  Divider,
  Flex,
  Button,
  useBreakpointValue,
} from '@chakra-ui/react';

import Spinner from '@inspire/ui/chakra/Spinner';
import Page from '@inspire/ui/chakra/layout/Page';

import PisteItem from 'components/piste/PisteItem';
import {
  filterPistesReco,
  orderPistesByScore,
} from '@inspire/data/helpers/algo';

import EclaireurItem from 'components/eclaireur/EclaireurItem';
import Slider from 'components/general/Slider';
import Link from 'components/general/Link';
import LinkButton from 'components/general/LinkButton';
import { useInfiniteEclaireurs, useMyProfile, usePistes } from 'lib/hooks';
import {
  ECHANGER_ECLAIREUR_PATH,
  eclaireurPath,
  ECLAIREURS_PATH,
  pistePath,
  PISTES_PATH,
} from 'lib/paths';

import ProjetOrientation from 'components/general/ProjetOrientation';
import Span from '@inspire/ui/chakra/Span';
import DevenirEclaireurContent from 'components/lyceen/DevenirEclaireurContent';
import PisteFeedbackForAllPistes from 'components/piste/PisteFeedbackForAllPistes';

import {
  GTM_CATEGORY_QUESTIONNAIRE,
  GTM_ACTION_VOIR_PISTES,
  pushEvent,
  GTM_CATEGORY_ECLAIREURS,
  GTM_ACTION_ECLAIREUR_ACCUEIL_VOIR_PROFIL,
} from 'lib/gtm';
import EclaireursMobiliteGroupContact from 'components/eclaireur/EclaireursMobiliteGroupContact';
import ModalTrigger from '@inspire/ui/chakra/modal/ModalTrigger';
import HowAreTheResultsCalculatedModal from './HowAreTheResultsCalculatedModal';

const LyceenDashboardPage = ({ isUserLoading }) => {
  const { myProfile } = useMyProfile();
  const { pistes, arePistesLoading } = usePistes();
  const pistesList = orderPistesByScore(
    filterPistesReco(myProfile.algoResults),
    pistes
  );
  const { eclaireurs, isFetching: areEclaireursLoading } =
    useInfiniteEclaireurs();

  const {
    eclaireurs: eclaireursMobiliteGroup,
    isFetching: areEclaireursGroupLoading,
  } = useInfiniteEclaireurs({}, {}, 'Mobilité');

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Page
      isLoading={
        isUserLoading ||
        arePistesLoading ||
        areEclaireursLoading ||
        areEclaireursGroupLoading
      }
      title="Accueil"
    >
      {myProfile?.niveau !== 'Post-bac' ? (
        <Box textAlign="center">
          <Box>
            <Flex>
              <Heading
                size="lg"
                textAlign="left"
                display="flex"
                alignItems="center"
              >
                <Span
                  color="yellow.500"
                  fontSize="6xl"
                  mr="4"
                  textShadow="1px 1px 1px #CCB200"
                >
                  1.
                </Span>
                <Span>
                  Découvre le résultat du quiz sur tes pistes d’études
                  recommandées
                </Span>
              </Heading>
            </Flex>
            <ModalTrigger
              trigger={(openModal) => (
                <Box textAlign="center" mb={4}>
                  <Button
                    onClick={() => {
                      openModal();
                    }}
                    colorScheme="black"
                  >
                    Comment sont calculés tes résultats ?
                  </Button>
                </Box>
              )}
            >
              {(closeModal) => (
                <HowAreTheResultsCalculatedModal
                  onClose={closeModal}
                  isMobile={isMobile}
                />
              )}
            </ModalTrigger>
            {arePistesLoading ? (
              <Spinner m={4} size="xl" thickness="4px" />
            ) : (
              <Slider>
                {pistesList?.slice(0, 10).map((p, i) => (
                  <Box key={p._id} pos="relative" role="group" p={4}>
                    <Link
                      key={i}
                      href={pistePath(p.slug)}
                      style={{ textDecoration: 'none' }}
                    >
                      <PisteItem piste={p} />
                    </Link>
                  </Box>
                ))}
              </Slider>
            )}
            <Box textAlign="center">
              <LinkButton
                m={4}
                href={PISTES_PATH}
                onClick={() => {
                  pushEvent(GTM_CATEGORY_QUESTIONNAIRE, GTM_ACTION_VOIR_PISTES);
                }}
              >
                Voir plus de pistes
              </LinkButton>
              <Flex justifyContent="center" mt={5}>
                <PisteFeedbackForAllPistes />
              </Flex>
            </Box>
          </Box>

          <Divider my={8} />

          <Box>
            <Heading size="lg" textAlign="left" mb={8}>
              <Span
                fontSize="4xl"
                color="yellow.500"
                mb="2"
                display="inline-block"
                textShadow="1px 1px 1px #CCB200"
              >
                Tu penses déménager ?
              </Span>
              <br />
              <Span>
                As-tu pensé à tout ? Les Éclaireurs sont là pour t’aiguiller.
              </Span>
            </Heading>
            <EclaireursMobiliteGroupContact
              areEclaireursLoading={areEclaireursLoading}
              eclaireurs={eclaireursMobiliteGroup}
            />
          </Box>
          <Divider my={8} />
          <Box>
            <Heading
              size="lg"
              textAlign="left"
              display="flex"
              alignItems="center"
            >
              <Span
                color="yellow.500"
                fontSize="6xl"
                mr="4"
                textShadow="1px 1px 1px #CCB200"
              >
                2.
              </Span>{' '}
              <Span>Contacte les étudiants Éclaireurs</Span>
            </Heading>
            {areEclaireursLoading ? (
              <Spinner m={4} size="xl" thickness="4px" />
            ) : (
              <Slider>
                {eclaireurs?.map((e, i) => (
                  <Box key={e._id} pos="relative" role="group" p={4}>
                    <Link
                      key={i}
                      href={eclaireurPath(e._id)}
                      style={{ textDecoration: 'none' }}
                      onClick={() => {
                        pushEvent(
                          GTM_CATEGORY_ECLAIREURS,
                          GTM_ACTION_ECLAIREUR_ACCUEIL_VOIR_PROFIL,
                          e._id
                        );
                      }}
                    >
                      <EclaireurItem eclaireur={e} group="All" />
                    </Link>
                  </Box>
                ))}
              </Slider>
            )}
            <Box textAlign="center">
              <LinkButton m={4} href={ECLAIREURS_PATH}>
                Voir tous les Éclaireurs
              </LinkButton>
              <LinkButton
                m={4}
                href={ECHANGER_ECLAIREUR_PATH}
                variant="solid"
                colorScheme="black"
              >
                Pourquoi contacter des Éclaireurs
              </LinkButton>
            </Box>
          </Box>

          <Divider my={8} />

          <Box>
            <Heading size="lg" textAlign="left" mb={8}>
              <Span
                fontSize="4xl"
                color="yellow.500"
                mb="2"
                display="inline-block"
                textShadow="1px 1px 1px #CCB200"
              >
                Un peu perdu ?
              </Span>
              <br />
              <Span>Fais le point sur ton projet d'orientation</Span>
            </Heading>
            <ProjetOrientation />
          </Box>
        </Box>
      ) : (
        <DevenirEclaireurContent />
      )}
    </Page>
  );
};

export default LyceenDashboardPage;
